import React, { useEffect, useState } from 'react';

import { PreQualificationStep, setPreQualificationStep } from 'actions';
import { AccountActionType } from 'actions/account';
import CodeVerificationForm from 'components/CodeVerificationForm';
import Loader from 'components/Loader';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'hooks/useAppSelector';
import { FailureNotificationType } from 'notifications';
import * as notificationManager from 'notifications';
import UpdateEmailPopup from 'pages/common/UpdateEmailPopup/UpdateEmailPopup';
import { checkBorrower } from 'utils/checkBorrower';
import { applicationApi } from 'api/ApplicationApi';
import { authApi } from 'api/auth/AuthApi';

import styles from './EnterConfirmationCode.module.scss';

const EnterConfirmationCode = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    account: { email },
    loading: { [AccountActionType.ConfirmNewPassword]: isCodeVerifying },
    coBorrower: { email: coborrowerEmail },
    borrower: { isHomeImprovementSameAsPrimaryAddress, isLocked },
    application: { applicationId, [LoanInformationVariables.LoanAmount]: loanAmount },
  } = useAppSelector((state) => state);

  const [isUpdateEmailPopupOpen, setIsUpdateEmailPopupOpen] = useState(false);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [isCodeValid, setIsCodeValid] = useState<boolean | null>(null);

  const handleResendCode = async () => {
    setIsCodeValid(null);
    authApi.sendEmailCode();
  };

  const handleTimerChange = (isRunning: boolean) => {
    setIsTimerActive(isRunning);
  };

  useEffect(() => {
    handleResendCode();
  }, []);

  const redirect = async () => {
    try {
      setIsLoading(true);

      const { borrowerApplication, hasCobborowerApplication } = await applicationApi.getActiveApplication();

      if (isLocked && !borrowerApplication && !hasCobborowerApplication) {
        dispatch(setPreQualificationStep(PreQualificationStep.NotAbleToAccept));

        return;
      }

      checkBorrower({
        borrowerApplication: borrowerApplication,
        hasCobborowerApplication: hasCobborowerApplication,
        dispatch: dispatch,
        navigator: navigator,
        nextStep:
          // Workaround: Checking `isHomeImprovementSameAsPrimaryAddress !== null`
          // due to the lack of a centralized Sign-In Routing Manager.
          // To be refactored during the global Sign-In routing overhaul.
          isHomeImprovementSameAsPrimaryAddress !== null
            ? PreQualificationStep.BorrowerIncome
            : PreQualificationStep.BorrowerInfo,
        isForceRedirectToDashboard: !!applicationId || !loanAmount,
      });
    } catch (error: any) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmailCode = async (code: string) => {
    try {
      const response = await authApi.verifyEmailCode(code);

      setIsCodeValid(response.success);

      if (response.success) {
        await redirect();
      }
    } catch (error) {
      setIsCodeValid(false);
    }
  };

  const openUpdateEmailPopup = () => {
    setIsUpdateEmailPopupOpen(true);
  };

  const closeUpdateEmailPopup = () => {
    setIsUpdateEmailPopupOpen(false);
  };

  const handleEmailUpdated = async () => {
    setIsUpdateEmailPopupOpen(false);
    await redirect();
  };

  const validateEmail = (value: string) => {
    if (value === coborrowerEmail) {
      return 'Email should not match with Co-borrower Email';
    }
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className={styles.container}>
          <CodeVerificationForm
            title="Email Verification"
            subtitle={`Let's verify your Email.\nWe've sent a 6-digit verification code to ${email}`}
            handleVerificationCodeComplete={handleVerifyEmailCode}
            isCodeVerifying={isCodeVerifying}
            isCodeValid={isCodeValid}
            onResendCode={handleResendCode}
            onTimerChange={handleTimerChange}
            formBodyClassName={styles.codeVerificationForm}
          />
          {!isTimerActive && (
            <div className={styles.changeEmailMessage}>
              In case you need to use a different email address, click{' '}
              <span className={styles.changeEmailLink} onClick={openUpdateEmailPopup}>
                here
              </span>
              .
            </div>
          )}
          {isUpdateEmailPopupOpen && (
            <UpdateEmailPopup
              isOpen={isUpdateEmailPopupOpen}
              onClose={closeUpdateEmailPopup}
              onSuccess={handleEmailUpdated}
              validateEmail={validateEmail}
            />
          )}
        </div>
      )}
    </>
  );
};

export default EnterConfirmationCode;
